import {
  AU_IN_METRES,
  SEMI_MAJOR_AXIS_MIN_AU,
  SEMI_MAJOR_AXIS_MAX_AU
} from '../state/selectors';

/**
 * ui-boundary formatting helpers (AD-5): SI in, presentation out. Conversion to
 * AU, days, and years happens ONLY here at the readout boundary; `core` and
 * `state` stay SI/radians. `AU_IN_METRES` is sourced through the `state` layer
 * re-export (never a `core` import, AD-2 layer table), so the single canonical
 * conversion factor is not duplicated as a `ui` literal.
 */

/** Day/year display constants: permitted in `ui`, banned in `core` (AD-5). */
export const SECONDS_PER_DAY = 86400;
export const DAYS_PER_YEAR = 365.25;

/**
 * Permitted editable semi-major-axis range, in AU (AC 1, 5, 6). Sourced from the
 * canonical `core/ranges.ts` THROUGH the `state` layer re-export (never a `core`
 * import, AD-2 layer table), and re-exported here under the pre-existing names so
 * `DetailPanel.svelte` keeps compiling unchanged. Single source: these are provably
 * the reducer's / codec's clamp bounds.
 */
export const MIN_SEMI_MAJOR_AXIS_AU = SEMI_MAJOR_AXIS_MIN_AU;
export const MAX_SEMI_MAJOR_AXIS_AU = SEMI_MAJOR_AXIS_MAX_AU;

/**
 * The one accessible validation message for the semi-major-axis range, sourced
 * from one constant so the live-region announcement and any test assert the
 * identical string (AC 6, AD-8).
 */
export const SEMI_MAJOR_AXIS_RANGE_MESSAGE =
  'Please enter a semi-major axis between 0.01 and 100 AU.';

/** SI metres -> AU number (unformatted), for driving the AU editor value. */
export function metresToAu(metres: number): number {
  return metres / AU_IN_METRES;
}

/** AU number -> SI metres, applied once before dispatching SetSemiMajorAxis. */
export function auToMetres(au: number): number {
  return au * AU_IN_METRES;
}

/** SI metres -> a labelled AU string, e.g. "1.000 AU". */
export function formatAu(metres: number): string {
  return `${metresToAu(metres).toFixed(3)} AU`;
}

/** SI metres per second -> a labelled km/s string. */
export function formatKmPerSec(metresPerSecond: number): string {
  return `${(metresPerSecond / 1000).toFixed(2)} km/s`;
}

/**
 * SI seconds -> a labelled period string. Deterministic unit threshold (Task 6):
 * present in YEARS when the period is at least one year (365.25 days), else in
 * DAYS. The 0.01-100 AU range spans roughly 0.37 days to about 1000 years, so
 * both units are exercised.
 */
export function formatPeriod(seconds: number): string {
  const days = seconds / SECONDS_PER_DAY;
  return days >= DAYS_PER_YEAR
    ? `${(days / DAYS_PER_YEAR).toFixed(2)} years`
    : `${days.toFixed(1)} days`;
}

/**
 * Orbit-shape word classifier, extracted so DetailPanel and system-summary share
 * one definition (behaviour identical to the prior duplicated inline expression;
 * thresholds and words unchanged).
 */
export function describeOrbitShape(e: number): string {
  return e === 0 ? 'circular' : e < 0.4 ? 'nearly circular' : 'elliptical';
}
